// Models
import { RuleMetadata, RtrDecision } from 'app/modules/rules/models';
import { TableConfig } from 'app/shared/pagination/models';

// Types
import { IRSchema } from 'app/modules/rulesAdvanced/models';

// Constants
import { PRESET_DATE_WITH_TIME_IN_UTC } from 'app/shared/constants';
import { FullRuleResponse } from 'app/modules/rules/types';
import { RuleType } from 'app/modules/detectionModels/models';

export const INITIAL_RULE_ID = -1;

export const TIME_STRING_FORMAT = 'dd MMM yyyy HH:mm';
export const DEFAULT_DATA_MIGRATION_LAG = 720;
export const STRIDE_WITH_NO_WINDOW: string = 'stride_with_no_window';
export const EXECUTION_FREQUENCY_OVERLAP_PERCENT_DEFAULT: number = 2;

export const RULES_TABLE_CONFIG: TableConfig[] = [
  {
    key: 'id',
    type: 'text',
    label: 'ID',
    collapsing: true,
  },
  {
    key: 'created_at',
    type: 'datetime',
    label: 'Created At',
    collapsing: true,
  },
  {
    key: 'title',
    type: 'text',
    label: 'Title',
  },
  {
    key: 'progress',
    type: 'rule_validation_progress',
    label: 'Progress',
    collapsing: true,
    sortable: false,
  },
  {
    key: 'status',
    type: 'label',
    label: 'Status',
    collapsing: true,
    sortable: false,
  },
  {
    key: 'completed_at',
    type: 'datetime',
    label: 'Completed At',
    collapsing: true,
    sortable: true,
  },
  {
    key: 'created_by_readable',
    type: 'text',
    label: 'Created By',
    collapsing: true,
    sortable: false,
  },
];

export const WHITELISTED_RULES_TABLE_CONFIG: TableConfig[] = [
  ...RULES_TABLE_CONFIG,
  {
    key: 'expiration',
    type: 'datetime',
    label: 'Expires',
    collapsing: true,
  },
];

export const INSIGHTS_RULES_TABLE_CONFIG: TableConfig[] = [
  {
    key: 'id',
    type: 'text',
    label: 'ID',
  },
  {
    key: 'deploy_start_date',
    type: 'datetime',
    label: 'Deployed On',
  },
  {
    key: 'title',
    type: 'text',
    label: 'Title',
  },
  {
    key: 'status',
    type: 'label',
    label: 'Status',
  },
  {
    key: 'created_by_readable',
    type: 'text',
    label: 'Created By',
  },
];

export const INITIAL_TRIGGERED_ALERT_PAGINATION_SETTINGS = {
  pageLimit: 25,
  totalPages: 0,
  pageOffset: 1,
};

export const MINUTE_IN_MILLISECONDS: number = 60000;
export const VALIDATION_WINDOW_LIMIT: number = 1000;
export const ACTIVATION_WINDOW_LIMIT: number = 100;

export const INITIAL_METADATA: RuleMetadata = {
  title: '',
  description: '',
  exec_start_date_time: PRESET_DATE_WITH_TIME_IN_UTC,
  exec_end_date_time: '',
  customer_support_involved: false,
  visible_to_child_org: false,
};

export const INITIAL_FILTERS = {
  raw_sql: '',
  query_tree: {} as any,
  inclusion_tags: [],
  exclusion_tags: [],
  inclusion_tag_names: [],
  exclusion_tag_names: [],
  aggregate_query_tree: {},
};

export const READ_ONLY_QUERY_BUILDER_SETTINGS = {
  immutableGroupsMode: true,
  immutableFieldsMode: true,
  immutableOpsMode: true,
  immutableValuesMode: true,
  canReorder: false,
  canRegroup: false,
};

export const DECISION_MAP: Record<RtrDecision, string> = {
  allow: 'Allow',
  deny: 'Deny',
  error: 'Error',
  step_up_authentication: 'Step up authentication',
};

export const RULE_DECISION_OPTIONS = [
  {
    text: DECISION_MAP.deny,
    value: RtrDecision.DENY,
  },
  {
    text: DECISION_MAP.step_up_authentication,
    value: RtrDecision.STEP_UP_AUTHENTICATION,
  },
];

export const FLOW_RULE_DECISION_OPTIONS = [
  ...RULE_DECISION_OPTIONS,
  {
    text: DECISION_MAP.allow,
    value: RtrDecision.ALLOW,
  },
];

export const initialCurrentRule: FullRuleResponse = {
  total_alerts: 0,
  open_cases: 0,
  closed_cases: 0,
  currency: '',
  scenario: '',
  source: 'INTERNAL',
  amount_volume: 0,
  comments: [],
  override_qbc: null,
  execution_frequency_behavior: null,
  content: {
    filters: {
      aggregate_query_tree: {},
      query_tree: {},
      hashed_aggregate_query_tree: '',
      hashed_query_tree: '',
      hashed_tags: '',
      inclusion_tags: [],
      exclusion_tags: [],
      inclusion_tag_names: [],
      exclusion_tag_names: [],
    },
    scenario: {
      hashed_parameters: '',
      name: '',
      parameters: {},
      filters: {},
    },
    specification: {} as IRSchema,
  },
  execution_frequency: '',
  readable_execution_frequency: '',
  execution_delay_in_minutes: 0,
  is_makeup_execution_enabled: false,
  generates_alerts: true,
  is_synchronous: false,
  created_at: '',
  created_by_readable: '',
  created_by_id: null,
  deploy_start_date: '',
  deploy_end_date: '',
  derived_from_rule: null,
  derived_rules: [],
  description: '',
  editors: [],
  id: INITIAL_RULE_ID,
  notes: '',
  num_alerts: 0,
  num_entities: 0,
  num_txns: 0,
  num_instruments: 0,
  queue: INITIAL_RULE_ID,
  queue_title: null,
  alert_priority: null,
  status: '',
  rule_flow_status: null,
  rule_type: RuleType.STANDARD,
  tags: [],
  title: '',
  watchers: [],
  customer_support_involved: false,
  execution_mode: '',
  template: null,
  runs_on_org_id: null,
  visible_to_child_org: false,
  org_id: 0,
  runs_on_org: null,
  runs_independently: null,
  relevance_score: null,
  'created_by.full_name': '',
  open_alerts: null,
  closed_alerts: null,
  closed_alert_to_case: null,
  closed_alert_to_sar: null,
  average_alerts_dispositioned_daily: null,
  options: {},
};

export const RULE_PRIORITY_ALERT_INTERACTION_EXPLANATION =
  "When multiple rules with different queues are triggered on the same event, Rule Priority determines which queue the alert will be sent to. When multiple rules with different queues have the same priority, the most recently created rule's queue will be used.";

/**
 * serial_number_out_of_range scenario only.
 *
 * Maps the "entity" dropdown values to their corresponding group_type.
 * The keys must match the values in pensieve/scenario_configs/serial_number_out_of_range.json
 */
export const SERIAL_NUMBER_ENTITY_TO_GROUP_TYPE: Record<string, string> = {
  'check depositor': 'txn_event.receiver_entity_id',
  'check issuer': 'txn_event.sender_entity_id',
  'depositing account': 'txn_event.receiver_instrument_id',
  'issuing account': 'txn_event.sender_instrument_id',
};
